import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { Globe, Map, Share2, Award, CheckCircle2, Briefcase, Timer, TrendingUp, Server } from 'lucide-react';
import AboutIntro from '../components/AboutIntro';
import ToolShowcase from '../components/ToolShowcase';

export const About = () => {
  const { t, i18n } = useTranslation();
  const isIt = i18n.language === 'it';

  const languages = [
    { name: t('about.languages.italian.name'), level: t('about.languages.italian.level'), flag: '🇮🇹' },
    { name: t('about.languages.romanian.name'), level: t('about.languages.romanian.level'), flag: '🇷🇴' },
    { name: t('about.languages.english.name'), level: t('about.languages.english.level'), flag: '🇬🇧' },
    { name: t('about.languages.russian.name'), level: t('about.languages.russian.level'), flag: '🇷🇺' }
  ];

  const highlights = [
    { it: '20+ progetti', en: '20+ projects' },
    { it: '−40% tempi gestione', en: '−40% handling time' },
    { it: '+35% lead medi', en: '+35% avg leads' },
    { it: 'Azure · Supabase · n8n', en: 'Azure · Supabase · n8n' }
  ];

  const impactCards = [
    { title: t('about.businessImpact.sections.automation.title'), desc: t('about.businessImpact.sections.automation.description') },
    { title: t('about.businessImpact.sections.analytics.title'), desc: t('about.businessImpact.sections.analytics.description') },
    { title: t('about.businessImpact.sections.workflow.title'), desc: t('about.businessImpact.sections.workflow.description') }
  ];

  return (
    <div className="pt-24 pb-12 px-4 bg-white dark:bg-gray-900 min-h-screen">
      <SEO
        title={`${isIt ? 'Chi Sono' : 'About'} – Victor Lazari`}
        description={isIt ? 'Chi è Victor Lazari: consulente AI e automazione.' : 'About Victor Lazari: AI and automation consultant.'}
        canonical={"https://victorlazari.com/about"}
        image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
        type="website"
      />
      <AboutIntro />

      {/* Overview mini-dashboard */}
      <section className="max-w-6xl mx-auto mt-8">
        <div className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-gray-100 mb-4">{t('about.overview.title') as string}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[{ k: 'projects', Icon: Briefcase }, { k: 'timeSaved', Icon: Timer }, { k: 'leads', Icon: TrendingUp }, { k: 'stack', Icon: Server }].map(({ k, Icon }: any) => (
              <div key={k} className="rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-700/50 p-4">
                <div className="flex items-center gap-2 text-slate-800 dark:text-gray-200">
                  <div className="w-7 h-7 rounded-full grid place-items-center bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="text-[12px]">{t(`about.overview.metrics.${k}.label`) as string}</div>
                </div>
                <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-gray-100">{t(`about.overview.metrics.${k}.value`) as string}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-8">
        <ToolShowcase />
      </div>

      {/* IT-as-a-Service positioning */}
      <section className="max-w-6xl mx-auto mt-10">
        <div className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 md:p-8">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 dark:text-gray-100 mb-4">{t('about.itaas.title') as string}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4 text-slate-700 dark:text-gray-300">
              {((t('about.itaas.paragraphs', { returnObjects: true }) as string[]) || []).map((p, i) => (
                <p key={i} className="leading-relaxed">{p}</p>
              ))}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {((t('about.itaas.bullets', { returnObjects: true }) as string[]) || []).map((b, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-700/50 p-3 text-sm flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5" />{b}</div>
                ))}
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100 mt-6">{t('about.itaas.whyTitle') as string}</h3>
              <p className="leading-relaxed">{t('about.itaas.whyText') as string}</p>

              <div className="mt-4 p-4 rounded-md bg-slate-50 dark:bg-gray-700/50 border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-gray-200">
                {t('about.itaas.cta') as string}
              </div>
            </div>
            <div className="lg:col-span-1">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100">{t('about.itaas.tableTitle') as string}</h3>
              <div className="mt-3 space-y-3">
                {((t('about.itaas.table', { returnObjects: true }) as any[]) || []).map((row, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 dark:border-gray-700 p-3 bg-slate-50 dark:bg-gray-700/50">
                    <div className="text-sm font-semibold text-slate-900 dark:text-gray-100">{row.level}</div>
                    <div className="text-sm text-slate-700 dark:text-gray-300">{row.result}</div>
                    <div className="text-[12px] text-slate-500 dark:text-gray-400">{row.tech}</div>
                  </div>
                ))}
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100 mt-6">{t('about.itaas.summaryTitle') as string}</h3>
              <ul className="mt-2 space-y-1">
                {((t('about.itaas.summary', { returnObjects: true }) as string[]) || []).map((s, i) => (
                  <li key={i} className="text-sm text-slate-700 dark:text-gray-300 flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5" />{s}</li>
                ))}
              </ul>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100 mt-6">{t('about.itaas.visionTitle') as string}</h3>
              <p className="text-sm text-slate-700 dark:text-gray-300">{t('about.itaas.visionText') as string}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-6xl mx-auto mt-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {highlights.map((h, i) => (
            <div key={i} className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-center">
              <p className="text-lg font-semibold text-slate-900 dark:text-gray-100">{isIt ? h.it : h.en}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Impact */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto my-12"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {impactCards.map((c, i) => (
            <div key={i} className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100 mb-2">{c.title}</h3>
              <p className="text-sm text-slate-700 dark:text-gray-300">{c.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Languages & Global */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto mb-16"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-slate-900 dark:text-gray-100 inline-flex items-center">
            <Globe className="w-8 h-8 mr-2" />
            {t('about.international.title')}
          </h2>
          <p className="text-lg text-slate-600 dark:text-gray-300">{t('about.international.subtitle')}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-stretch">
          <div className="w-full lg:w-1/2 bg-white dark:bg-gray-800 rounded-xl p-6 border border-slate-200 dark:border-gray-700 shadow-sm flex flex-col justify-center">
            <h3 className="text-xl font-semibold mb-6 text-slate-900 dark:text-gray-100 flex items-center">
              <Map className="mr-2" /> {t('about.international.experienceTitle')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {(['italy','romania','uk','global'] as const).map((k) => (
                <div key={k} className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-lg border border-slate-200 dark:border-gray-700">
                  <div className="text-4xl mb-2">{k==='italy'?'🇮🇹':k==='romania'?'🇷🇴':k==='uk'?'🇬🇧':'🌍'}</div>
                  <h4 className="font-semibold text-slate-900 dark:text-gray-100">{t(`about.international.countries.${k}.name`)}</h4>
                  <p className="text-sm text-slate-600 dark:text-gray-300">{t(`about.international.countries.${k}.description`)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-1/2 bg-white dark:bg-gray-800 rounded-xl p-6 border border-slate-200 dark:border-gray-700 shadow-sm flex flex-col">
            <h3 className="text-xl font-semibold mb-6 text-slate-900 dark:text-gray-100 flex items-center">
              <Share2 className="mr-2" /> {t('about.languages.title')}
            </h3>
            <div className="space-y-6 flex-1">
              {languages.map((language, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="text-4xl">{language.flag}</div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 dark:text-gray-100">{language.name}</h4>
                    <p className="text-sm text-slate-600 dark:text-gray-300 mt-1">{language.level}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-slate-50 dark:bg-gray-700/50 rounded-lg border border-slate-200 dark:border-gray-700">
              <h4 className="font-semibold flex items-center gap-2 text-slate-900 dark:text-gray-100">
                <Award className="text-sky-700 dark:text-sky-400" /> {t('about.languages.edge.title')}
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-slate-700 dark:text-gray-300">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5" /> {t('about.languages.edge.description')}</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto text-center mb-20"
      >
        <div className="bg-gradient-to-r from-cyan-900/50 dark:from-cyan-900/70 to-blue-900/50 dark:to-blue-900/70 rounded-xl p-8 border border-cyan-400/30 dark:border-cyan-500/50">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-cyan-400 dark:text-cyan-300">{t('about.cta.title')}</h2>
          <p className="text-xl text-gray-300 dark:text-gray-200 mb-8">{t('about.cta.subtitle')}</p>
          <a href="/contact" className="px-8 py-4 bg-cyan-500 dark:bg-cyan-600 text-white rounded-lg font-semibold inline-block hover:bg-cyan-600 dark:hover:bg-cyan-700">{t('about.cta.button')}</a>
        </div>
      </motion.section>
    </div>
  );
};